// This is the replacement for `RxDBHandler` in `src/rxdb/useRxDB.tsx`
//
// DataManagerHandler is the entry point for all RxDB replication management.
// It is built around `useReducer`, which in turn relies heavily on `State`, `Actions`, and `reducer`.
import * as Sentry from '@sentry/react';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useReducer } from 'react';

import { useFlags } from '@/featureFlags/useFlags';

import { Actions } from './actions/Actions';
import { kind as taskFailedKind, TaskFailed } from './actions/completions/TaskFailed';
import { startTask } from './actions/StartTask';
import { crumbleAction } from './bakery/actionBakery';
import { DataManager } from './DataManager';
import { dataManagerContext, initialState } from './dataManagerContext';
import { Dispatch } from './Dispatch';
import { logError } from './logError';
import { reducer } from './reducers/reducer';
import { crumble as crumbleState } from './State';

export const DataManagerHandler: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  // Note: the reducer passed to `useReducer` must be constant over multiple rerenders of DataManagerHandler.
  // If it changes, `useReducer()` will re-reduce the previous state and action with the new reducer
  // to guarantee that the new state is correct.
  // We trigger run-once activities (such as tasks and goal settling) during reduction and state transitions.
  // If the reducer changed at the same time, we would end up triggering these multiple times,
  // which would violate the assumptions of our system.
  // We can prevent this by memoizing the reducer with no dependencies,
  // ensuring that the reducer is _always_ the same instance.
  // `reducer` is typically a constant so this is technically unnecessary,
  // but `useMemo` ensures that this will remain stable regardless of the code changes.
  // Note that this means that `stableReducer` *cannot* have any dependencies.
  //
  // See also:
  // https://stackoverflow.com/questions/54892403/usereducer-action-dispatched-twice
  // https://stackoverflow.com/questions/55055793/react-usereducer-hook-fires-twice-how-to-pass-props-to-reducer/55056623#55056623
  const stableReducer = useMemo(() => reducer, []);
  const [state, baseDispatch] = useReducer(stableReducer, initialState);
  const { logSyncState } = useFlags();

  const dispatch = useCallback<Dispatch>(
    (action: Actions) => {
      baseDispatch(action);
      if (logSyncState) {
        Sentry.addBreadcrumb({
          category: `Action.${action.kind}`,
          data: {
            source: 'Action',
            ...crumbleAction(action),
          },
          level: 'debug',
          message: `Action.${action.kind}`,
          type: 'debug',
        });
      }
    },
    [baseDispatch, logSyncState],
  );

  useEffect(() => {
    if (logSyncState) {
      Sentry.addBreadcrumb({
        category: `State`,
        data: crumbleState(state),
        level: 'debug',
        message: `State Update`,
        type: 'debug',
      });
    }
  }, [state, logSyncState]);

  useEffect(() => {
    const { nextTask } = state;
    if (nextTask) {
      // When the `state` changes, if it has a task, execute it.
      // The task is allowed to `dispatch` additional actions,
      // thus further updating the state as necessary.
      // As the task is `async`, it may well live on after the `state` has transitioned.
      dispatch(startTask);
      nextTask?.start(dispatch).catch(reason => {
        // Task execution is occurring in a non-async `useEffect` callback,
        // so we need to ensure its rejections/exceptions are handled and logged.
        logError('Error executing task', reason, {
          state: crumbleState(state),
          task: nextTask.kind,
        });
        const taskFailed: TaskFailed = {
          kind: taskFailedKind,
          reason,
        };
        dispatch(taskFailed);
      });
    }
  }, [state, dispatch]);

  const manager: DataManager = { state, dispatch };

  return <dataManagerContext.Provider value={manager}>{children}</dataManagerContext.Provider>;
};
